import { Link } from "react-router-dom";
import { ArrowRight, Clock3, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EnquiryForm from "@/components/EnquiryForm";
import ClosingCTA from "@/components/ClosingCTA";
import { FadeUp, WA_LINK } from "@/components/Reveal";

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "WhatsApp",
    line: "+91 96619 68613 — fastest reply",
    href: WA_LINK,
    testid: "contact-channel-whatsapp",
    accent: "text-wa",
  },
  {
    icon: Phone,
    title: "Call Us",
    line: "+91 96619 68613 · +91 72800 03248",
    href: "tel:+919661968613",
    testid: "contact-channel-call",
    accent: "text-saffron",
  },
  {
    icon: Mail,
    title: "Email",
    line: "help@tasteriderestro.com",
    href: "mailto:help@tasteriderestro.com",
    testid: "contact-channel-email",
    accent: "text-saffron",
  },
  {
    icon: MapPin,
    title: "Head Office",
    line: "Beside Life Care Hospital, Bariatu Road, Ranchi 834009",
    href: "https://www.google.com/maps/search/?api=1&query=Taste+Ride+Bariatu+Road+Ranchi",
    testid: "contact-channel-hq",
    accent: "text-flame",
  },
];

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHeader
        testid="contact-header"
        eyebrow="Contact Us"
        title="Let's talk."
        accent="We answer fast."
        copy="Investor, partner, hospital or hungry — pick whichever channel suits you. WhatsApp is our home turf."
      />

      <section className="bg-obsidian px-5 pb-24 sm:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="space-y-4" data-testid="contact-channels">
              {CHANNELS.map((c, i) => (
                <FadeUp key={c.testid} delay={i * 0.08}>
                  <a
                    data-testid={c.testid}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-coal p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-saffron/40"
                  >
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/5 ${c.accent}`}>
                      <c.icon size={20} strokeWidth={1.8} />
                    </span>
                    <span>
                      <span className="block font-display text-base font-semibold text-cream">{c.title}</span>
                      <span className="mt-0.5 block text-sm text-slate-400">{c.line}</span>
                    </span>
                  </a>
                </FadeUp>
              ))}
              <FadeUp delay={0.35}>
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="contact-hours">
                  <Clock3 size={18} className="text-saffron" />
                  <p className="text-sm text-slate-300">
                    Outlets open daily, <span className="font-semibold text-cream">10 AM – 11 PM</span> · Hospital canteens 24/7
                  </p>
                </div>
              </FadeUp>
              <FadeUp delay={0.42}>
                <Link
                  data-testid="contact-franchisee-link"
                  to="/franchisee"
                  className="group flex items-center justify-between rounded-2xl border border-flame/30 bg-flame/10 p-5 transition-colors hover:bg-flame/20"
                >
                  <p className="text-sm text-slate-200">
                    Want to <span className="font-semibold text-cream">own a Taste Ride?</span> Franchisee enquiries go here
                  </p>
                  <ArrowRight size={18} className="shrink-0 text-flame transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </FadeUp>
            </div>
          </div>

          <FadeUp delay={0.15} className="lg:col-span-7">
            <EnquiryForm type="contact" testid="contact-form" />
          </FadeUp>
        </div>
      </section>

      <ClosingCTA />
    </main>
  );
}
